<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="Save & Share"
    width="500px"
    :before-close="close"
    custom-class="viq-share-dialog"
  >
    <div class="viq-share">
      <div class="viq-share__field">
        <label>Title</label>
        <el-input v-model="title" placeholder="Give this a name..." />
      </div>

      <div class="viq-share__field">
        <label>Visibility</label>
        <el-radio-group v-model="visibility">
          <el-radio label="private">Private (only me)</el-radio>
          <el-radio label="organisation">Organisation</el-radio>
          <el-radio label="public">Public (anyone with link)</el-radio>
        </el-radio-group>
      </div>

      <div class="viq-share__field">
        <label>Expiry</label>
        <el-select v-model="expiry" placeholder="Select expiry">
          <el-option label="Never" value="never" />
          <el-option label="24 hours" value="24h" />
          <el-option label="7 days" value="7d" />
          <el-option label="30 days" value="30d" />
          <el-option label="90 days" value="90d" />
        </el-select>
      </div>

      <div class="viq-share__field">
        <label>Auto-refresh</label>
        <el-select v-model="refreshFrequency" placeholder="Select frequency">
          <el-option label="None" value="none" />
          <el-option label="Daily" value="daily" />
          <el-option label="Weekly" value="weekly" />
          <el-option label="Monthly" value="monthly" />
        </el-select>
      </div>

      <div v-if="shareLink" class="viq-share__link">
        <label>Share Link</label>
        <div class="viq-share__link-row">
          <el-input :value="shareLink" readonly size="small" />
          <el-button size="small" @click="copyLink">Copy</el-button>
        </div>
      </div>

      <div v-if="embedCode" class="viq-share__embed">
        <label>Embed Code</label>
        <el-input :value="embedCode" readonly type="textarea" :rows="3" size="small" />
      </div>
    </div>

    <template slot="footer">
      <el-button @click="close">Cancel</el-button>
      <el-button type="primary" :loading="saving" @click="save">
        {{ shareLink ? 'Update' : 'Save & Generate Link' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ViqShareDialog",
  data() {
    return {
      title: "",
      visibility: "private",
      expiry: "30d",
      refreshFrequency: "none",
      shareLink: "",
      embedCode: "",
      saving: false,
    };
  },
  computed: {
    ...mapState("Viq", ["shareDialogOpen", "shareTarget"]),
    dialogVisible: {
      get() {
        return this.shareDialogOpen;
      },
      set(val) {
        if (!val) this.close();
      },
    },
  },
  watch: {
    shareDialogOpen(val) {
      if (val) {
        this.shareLink = "";
        this.embedCode = "";
        this.title = "";
      }
    },
  },
  methods: {
    close() {
      this.$store.dispatch("Viq/closeShareDialog");
    },
    async save() {
      this.saving = true;
      try {
        const artifact = await this.$store.dispatch("Viq/saveArtifact", {
          title: this.title.trim() || "Untitled",
          content: this.shareTarget?.content || "",
          type: this.shareTarget?.type || "answer",
          visibility: this.visibility,
          expiry: this.expiry,
          refresh_frequency: this.refreshFrequency,
          metadata: this.shareTarget,
        });

        if (artifact && artifact.slug) {
          const baseUrl = this.$config.clientURL || window.location.origin;
          this.shareLink = `${baseUrl}/viq/shared/${artifact.slug}`;
          this.embedCode = `<iframe src="${this.shareLink}?embed=true" width="100%" height="600" frameborder="0"></iframe>`;
          this.$notify({
            title: "Saved",
            message: "Artifact saved and share link generated.",
            type: "success",
            duration: 3000,
          });
        }
      } catch {
        this.$notify({
          title: "Error",
          message: "Failed to save artifact.",
          type: "error",
          duration: 3000,
        });
      } finally {
        this.saving = false;
      }
    },
    copyLink() {
      if (!this.shareLink) return;
      navigator.clipboard.writeText(this.shareLink).then(() => {
        this.$notify({
          title: "Copied",
          message: "Link copied to clipboard.",
          type: "success",
          duration: 2000,
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-share {
  &__field {
    margin-bottom: $viq-space-md;

    label {
      display: block;
      font-size: $viq-font-sm;
      font-weight: 600;
      color: $viq-text-primary;
      margin-bottom: $viq-space-sm;
    }
  }

  &__link,
  &__embed {
    margin-top: $viq-space-md;
    padding-top: $viq-space-md;
    border-top: 1px solid $viq-border;

    label {
      display: block;
      font-size: $viq-font-sm;
      font-weight: 600;
      color: $viq-text-primary;
      margin-bottom: $viq-space-sm;
    }
  }

  &__link-row {
    display: flex;
    gap: $viq-space-sm;
  }
}
</style>
